import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Presentation, Plus, Upload, Eye, Trash2, ArrowLeft, FileText, Users, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminToolbox = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [toolboxTalks, setToolboxTalks] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedTalk, setSelectedTalk] = useState(null);
  const [talkForm, setTalkForm] = useState({
    title: '',
    description: '',
    content: '',
    category: ''
  });
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [toolboxSessions, setToolboxSessions] = useState([]);
  const [showSessionsDialog, setShowSessionsDialog] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [talkToSend, setTalkToSend] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadToolboxTalks();
    loadWorkers();
  }, []);

  const loadWorkers = async () => {
    try {
      const response = await axiosInstance.get('/companies/employees');
      const workerList = response.data.filter(emp => emp.role === 'worker');
      const supervisorList = response.data.filter(emp => emp.role === 'supervisor');
      setWorkers(workerList);
      setSupervisors(supervisorList);
    } catch (error) {
      console.error('Failed to load workers:', error);
    }
  };

  const loadToolboxTalks = async () => {
    try {
      console.log('📡 Fetching toolbox talks...');
      const response = await axiosInstance.get('/toolbox-talks');
      console.log('✅ Toolbox talks response:', response.data?.length, 'talks received');
      console.log('Sample talk:', response.data?.[0]?.title);
      setToolboxTalks(response.data);
    } catch (error) {
      console.error('❌ Failed to load toolbox talks:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const handleCreateTalk = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/admin/toolbox-talks', talkForm);
      toast.success(t('adminToasts.ToolboxTalkCreatedSuccessfully'));
      setShowCreateDialog(false);
      setTalkForm({ title: '', description: '', content: '', category: '' });
      loadToolboxTalks();
    } catch (error) {
      console.error('Failed to create toolbox talk:', error);
      toast.error(error.response?.data?.detail || 'Failed to create toolbox talk');
    }
  };

  const handleUploadPdf = async (e) => {
    e.preventDefault();
    if (!selectedPdf) {
      toast.error(t('adminToasts.PleaseSelectAPdfFile'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedPdf);
      formData.append('title', talkForm.title);
      formData.append('description', talkForm.description);
      formData.append('category', talkForm.category);

      await axiosInstance.post('/admin/toolbox-talks/upload-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t('adminToasts.ToolboxTalkPdfUploadedSuccessfully'));
      setShowUploadDialog(false);
      setSelectedPdf(null);
      setTalkForm({ title: '', description: '', content: '', category: '' });
      loadToolboxTalks();
    } catch (error) {
      console.error('Failed to upload toolbox talk PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTalk = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

    try {
      await axiosInstance.delete(`/admin/toolbox-talks/${id}`);
      toast.success(t('adminToasts.ToolboxTalkDeletedSuccessfully'));
      loadToolboxTalks();
    } catch (error) {
      console.error('Failed to delete toolbox talk:', error);
      toast.error(t('adminToasts.FailedToDeleteToolboxTalk'));
    }
  };

  const handleViewSessions = async (talkId) => {
    try {
      const response = await axiosInstance.get(`/toolbox-talk-sessions?talk_id=${talkId}`);
      setToolboxSessions(response.data);
      setShowSessionsDialog(true);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      toast.error(t('adminToasts.FailedToLoadToolboxTalkSessions'));
    }
  };

  const handleSeedTalks = async () => {
    if (!window.confirm('This will seed the database with 100+ pre-made toolbox talks. Continue?')) return;
    
    setSeeding(true);
    try {
      const response = await axiosInstance.post('/admin/seed-toolbox-talks');
      toast.success(response.data.message || 'Toolbox talks seeded successfully!');
      loadToolboxTalks(); // Reload the talks list
    } catch (error) {
      console.error('Failed to seed toolbox talks:', error);
      toast.error(error.response?.data?.detail || 'Failed to seed toolbox talks');
    } finally {
      setSeeding(false);
    }
  };

  const handleOpenSendDialog = (talk) => {
    setTalkToSend(talk);
    setSelectedWorkers([]);
    setShowSendDialog(true);
  };

  const handleToggleWorker = (workerId) => {
    setSelectedWorkers(prev => 
      prev.includes(workerId) 
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const handleSendToWorkers = async () => {
    if (selectedWorkers.length === 0) {
      toast.error(t('adminToasts.PleaseSelectAtLeastOneWorker'));
      return;
    }

    setSending(true);
    try {
      const response = await axiosInstance.post(
        `/admin/toolbox-talks/${talkToSend.id}/send`,
        { workerIds: selectedWorkers }
      );
      toast.success(response.data.message || 'Toolbox talk sent successfully!');
      setShowSendDialog(false);
      setTalkToSend(null);
      setSelectedWorkers([]);
    } catch (error) {
      console.error('Failed to send toolbox talk:', error);
      toast.error(error.response?.data?.detail || 'Failed to send toolbox talk');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('adminUI.ToolboxTalks')}</h1>
          <p className="text-gray-600 mt-1">{t('adminUI.ManageSafetyToolboxTalksAndTrainingSessions')}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Create New Talk
          </Button>
          <Button onClick={() => setShowUploadDialog(true)} variant="outline" className="gap-2">
            <Upload className="w-4 h-4" />
            Upload PDF
          </Button>
          <Button 
            onClick={handleSeedTalks} 
            variant="outline" 
            className="gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
            disabled={seeding}
          >
            <Presentation className="w-4 h-4" />
            {seeding ? 'Seeding...' : 'Load Pre-Made Library (100+ Talks)'}
          </Button>
        </div>

        {/* Toolbox Talks List */}
        <Card>
          <CardHeader>
            <CardTitle>All Toolbox Talks ({toolboxTalks.length})</CardTitle>
            <CardDescription>{t('adminUI.ManageAndTrackToolboxTalkSessions')}</CardDescription>
          </CardHeader>
          <CardContent>
            {toolboxTalks.length === 0 ? (
              <div className="text-center py-12">
                <Presentation className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">{t('adminUI.NoToolboxTalksYetCreateYourFirstOne')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('adminUI.Title')}</TableHead>
                    <TableHead>{t('adminUI.Category')}</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>{t('adminUI.Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {toolboxTalks.map((talk) => (
                    <TableRow key={talk.id}>
                      <TableCell className="font-medium">{talk.title}</TableCell>
                      <TableCell>
                        {talk.category && (
                          <Badge variant="outline">{talk.category}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={talk.pdfPath ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                          {talk.pdfPath ? <FileText className="w-3 h-3 mr-1" /> : null}
                          {talk.pdfPath ? 'PDF' : 'Text'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedTalk(talk);
                              setShowViewDialog(true);
                            }}
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => handleOpenSendDialog(talk)}
                            title="Send to workers"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewSessions(talk.id)}
                            title="View sessions"
                          >
                            <Users className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteTalk(talk.id, talk.title)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.CreateToolboxTalk')}</DialogTitle>
              <DialogDescription>{t('adminUI.CreateANewTextBasedToolboxTalk')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTalk} className="space-y-4">
              <div>
                <Label>{t('adminUI.Title')}</Label>
                <Input
                  value={talkForm.title}
                  onChange={(e) => setTalkForm({ ...talkForm, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.Category')}</Label>
                <Input
                  value={talkForm.category}
                  onChange={(e) => setTalkForm({ ...talkForm, category: e.target.value })}
                  placeholder="e.g., Fall Protection, PPE"
                />
              </div>
              <div>
                <Label>{t('adminUI.Description')}</Label>
                <Textarea
                  value={talkForm.description}
                  onChange={(e) => setTalkForm({ ...talkForm, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <Label>{t('adminUI.Content')}</Label>
                <Textarea
                  value={talkForm.content}
                  onChange={(e) => setTalkForm({ ...talkForm, content: e.target.value })}
                  rows={6}
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">{t('adminUI.Create')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Upload PDF Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.UploadToolboxTalkPdf')}</DialogTitle>
              <DialogDescription>{t('adminUI.UploadAPdfDocumentAsAToolboxTalk')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUploadPdf} className="space-y-4">
              <div>
                <Label>{t('adminUI.Title')}</Label>
                <Input
                  value={talkForm.title}
                  onChange={(e) => setTalkForm({ ...talkForm, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.Category')}</Label>
                <Input
                  value={talkForm.category}
                  onChange={(e) => setTalkForm({ ...talkForm, category: e.target.value })}
                  placeholder="e.g., Fall Protection"
                />
              </div>
              <div>
                <Label>{t('adminUI.Description')}</Label>
                <Textarea
                  value={talkForm.description}
                  onChange={(e) => setTalkForm({ ...talkForm, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <Label>{t('adminUI.PdfFile')}</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setSelectedPdf(e.target.files[0])}
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{selectedTalk?.title}</DialogTitle>
              <DialogDescription>
                {selectedTalk?.category && <Badge variant="outline" className="mr-2">{selectedTalk.category}</Badge>}
                {selectedTalk?.pdfPath ? 'PDF Document' : 'Text Content'}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {selectedTalk?.description && (
                <p className="text-gray-700 mb-4">{selectedTalk.description}</p>
              )}
              {selectedTalk?.content && (
                <div className="whitespace-pre-wrap text-gray-900 bg-gray-50 p-4 rounded-lg">
                  {selectedTalk.content}
                </div>
              )}
              {selectedTalk?.pdfPath && (
                <div className="text-center py-8">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-red-500" />
                  <p className="text-gray-600 mb-4">{t('adminUI.PdfDocument')}</p>
                  <Button onClick={() => window.open(selectedTalk.pdfPath, '_blank')}>
                    Open PDF
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Sessions Dialog */}
        <Dialog open={showSessionsDialog} onOpenChange={setShowSessionsDialog}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.ToolboxTalkSessions')}</DialogTitle>
              <DialogDescription>{t('adminUI.ViewAttendanceForThisToolboxTalk')}</DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {toolboxSessions.length === 0 ? (
                <p className="text-center text-gray-600 py-8">{t('adminUI.NoSessionsRecordedYet')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>{t('adminUI.Conductor')}</TableHead>
                      <TableHead>{t('adminUI.Location')}</TableHead>
                      <TableHead>{t('adminUI.Attendees')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {toolboxSessions.map((session) => (
                      <TableRow key={session.id}>
                        <TableCell>{new Date(session.conductedAt).toLocaleDateString()}</TableCell>
                        <TableCell>{session.conductedBy}</TableCell>
                        <TableCell>{session.location || 'N/A'}</TableCell>
                        <TableCell>
                          {session.attendees?.length || 0} workers
                          {session.manualAttendees?.length > 0 && ` + ${session.manualAttendees.length} manual`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSessionsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send to Workers Dialog */}
        <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.SendToolboxTalkToWorkers')}</DialogTitle>
              <DialogDescription>
                {talkToSend?.title && `Sending: "${talkToSend.title}"`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-blue-900">{t('adminUI.WhoWillBeNotified')}</p>
                <div className="flex items-center gap-2 text-sm text-blue-800">
                  <Badge variant="outline" className="bg-blue-100">
                    ✓ {supervisors.length} Supervisor{supervisors.length !== 1 ? 's' : ''} (automatically)
                  </Badge>
                  <span className="text-gray-500">+</span>
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                    {selectedWorkers.length} Worker{selectedWorkers.length !== 1 ? 's' : ''} (selected below)
                  </Badge>
                </div>
                <p className="text-xs text-gray-600 italic">
                  All supervisors will be notified automatically. Select which workers should also receive this talk.
                </p>
              </div>
              
              {workers.length === 0 ? (
                <p className="text-center py-8 text-gray-500">{t('adminUI.NoWorkersFoundInYourCompany')}</p>
              ) : (
                <div className="max-h-96 overflow-y-auto border rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                    <Checkbox
                      checked={selectedWorkers.length === workers.length}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedWorkers(workers.map(w => w.id));
                        } else {
                          setSelectedWorkers([]);
                        }
                      }}
                    />
                    <span className="font-medium text-sm">Select All ({workers.length} workers)</span>
                  </div>
                  
                  {workers.map((worker) => (
                    <div key={worker.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                      <Checkbox
                        checked={selectedWorkers.includes(worker.id)}
                        onCheckedChange={() => handleToggleWorker(worker.id)}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{worker.name}</p>
                        <p className="text-xs text-gray-500">{worker.email}</p>
                        {worker.craft && (
                          <Badge variant="outline" className="text-xs mt-1">{worker.craft}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {selectedWorkers.length > 0 && (
                <p className="text-sm font-medium text-blue-600">
                  {selectedWorkers.length} worker(s) selected
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSendDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendToWorkers} 
                disabled={sending || selectedWorkers.length === 0}
                className="gap-2"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Sending...' : `Send to ${selectedWorkers.length} Worker(s) + ${supervisors.length} Supervisor(s)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminToolbox;
